export type TranslateFn = (key: string) => string

//## Translation value that marks a rule/paytable point as intentionally hide content (e.g. en_sc).
const SKIP_SENTINEL = '--skip--'

const isSkipText = (value: unknown): boolean => {
	if (typeof value !== 'string') {
		return false
	}

	const trimmed = value.trim()
	return trimmed === SKIP_SENTINEL || trimmed.length === 0
}

// Logic: skips the content when the skip text (`'--skip--'`) is present, including empty rows
// that would otherwise leave a gap after a skipped line is translated away.
const isSkipped = (value: unknown): boolean => {
	if (isSkipText(value)) {
		return true
	}

	if (typeof value === 'object' && value !== null) {
		const text = (value as Record<string, unknown>).text
		if (isSkipText(text)) {
			return true
		}
		if (Array.isArray(text) && text.length === 0) {
			return true
		}
	}

	return false
}

/** Documented default when init has not provided an RTP yet. */
export const DEFAULT_GAME_RTP = 94.68

export const formatGameRtp = (rtp: unknown, fallback: number = DEFAULT_GAME_RTP): string => {
	const parsed = Number(rtp)
	if (!Number.isFinite(parsed) || parsed <= 0) {
		return formatGameRtp(fallback, fallback)
	}

	// Keep the server precision when it is already two decimals; avoid trailing noise.
	const rounded = Math.round(parsed * 100) / 100
	return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2)
}

export const applyRtpPlaceholder = <T>(value: T, rtpDisplay: string): T => {
	if (typeof value === 'string') {
		return value.replaceAll('{{RTP}}', rtpDisplay) as T
	}

	if (Array.isArray(value)) {
		return value.map((item) => applyRtpPlaceholder(item, rtpDisplay)) as T
	}

	if (value && typeof value === 'object') {
		return Object.fromEntries(
			Object.entries(value as Record<string, unknown>).map(([key, item]) => [
				key,
				applyRtpPlaceholder(item, rtpDisplay),
			])
		) as T
	}

	return value
}

export const localizeConfig = <T>(value: T, translate: TranslateFn): T => {
	if (typeof value === 'string') {
		return (translate(value) || value) as T
	}

	if (Array.isArray(value)) {
		return value
			.map((item) => localizeConfig(item, translate))
			.filter((item) => !isSkipped(item)) as T
	}

	if (value && typeof value === 'object') {
		return Object.fromEntries(
			Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, localizeConfig(item, translate)])
		) as T
	}

	return value
}